/**
 * sessionPersist — persistă sesiunea (user + cheie master + keypair +
 * expires_at) în `sessionStorage`, scoped pe tab.
 *
 * Tradeoff acceptat: tab-scoped, clear automat la close tab. Supraviețuiește
 * refresh-ului, NU supraviețuiește închiderii tab-ului sau a browserului.
 * NU folosim `localStorage` pentru chei — ar persista permanent pe disc.
 *
 * Regulă absolută: doar `sessionStorage`, niciodată `localStorage`.
 */
import { bytesToBase64, base64ToBytes } from '@/crypto/sodium';
import type { KeyPair } from '@/crypto/sodium';
import type { TenantMembership } from '@/api/auth';

const STORAGE_KEY = 'cerbes-session-v1';

interface PersistedShape {
  user: {
    id: string;
    email: string;
    tenants: TenantMembership[];
  };
  currentTenantId: string | null;
  masterKey: string;
  publicKey: string;
  privateKey: string;
  expiresAt: string | null;
}

export interface PersistedSession {
  user: PersistedShape['user'];
  currentTenantId: string | null;
  masterKey: Uint8Array;
  keyPair: KeyPair;
  expiresAt: Date | null;
}

export function saveSession(s: {
  user: PersistedShape['user'];
  currentTenantId: string | null;
  masterKey: Uint8Array;
  keyPair: KeyPair;
  expiresAt: string | null;
}): void {
  try {
    const payload: PersistedShape = {
      user: s.user,
      currentTenantId: s.currentTenantId,
      masterKey: bytesToBase64(s.masterKey),
      publicKey: bytesToBase64(s.keyPair.publicKey),
      privateKey: bytesToBase64(s.keyPair.privateKey),
      expiresAt: s.expiresAt,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage indisponibil (mod privat strict, quota) — ignore.
    // UX: utilizatorul se va reloghi la refresh.
  }
}

export function loadSession(): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PersistedShape;
    if (!p.user?.id || !p.masterKey || !p.publicKey || !p.privateKey) {
      return null;
    }
    // Nu mai expirăm client-side pe expiresAt. Serverul e autoritatea:
    // cookie-ul se refresh-uiește la fiecare request (rolling session),
    // iar dacă sesiunea chiar a expirat, primul API call va returna 401
    // și handler-ul global (clearSession) va curăța sessionStorage.
    const expiresAt = p.expiresAt ? new Date(p.expiresAt) : null;
    return {
      user: p.user,
      currentTenantId: p.currentTenantId,
      masterKey: base64ToBytes(p.masterKey),
      keyPair: {
        publicKey: base64ToBytes(p.publicKey),
        privateKey: base64ToBytes(p.privateKey),
      },
      expiresAt,
    };
  } catch {
    return null;
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function updateCurrentTenant(tenantId: string | null): void {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const p = JSON.parse(raw) as PersistedShape;
    p.currentTenantId = tenantId;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    // ignore
  }
}
